// Common provider contracts and response validators for ADR-008
import { z } from "zod";
import { type ISO4217 } from "@/types/fx";

export type DailyRates = {
  base: ISO4217;
  date: Date;
  rates: Record<ISO4217, number>; // price of 1 base in target currency
  source: string;
};

export interface FxProvider {
  name: string;
  supportsHistorical: boolean;
  fetchDaily(
    base: ISO4217,
    date: Date | "latest",
    symbols?: ISO4217[],
  ): Promise<DailyRates>;
}

/**
 * ExchangeRate-API (v6) schemas
 * Docs: https://www.exchangerate-api.com/docs/free
 * Example:
 * {
 *   "result": "success",
 *   "base_code": "USD",
 *   "time_last_update_utc": "Fri, 01 Jan 2021 00:00:01 +0000",
 *   "conversion_rates": {"USD":1,"EUR":0.81,...}
 * }
 */
export const ExchangeRateApiSchema = z.object({
  result: z.literal("success"),
  base_code: z.string(),
  time_last_update_utc: z.string(),
  conversion_rates: z.object({}).catchall(z.number()),
});
export type ExchangeRateApiResponse = z.infer<typeof ExchangeRateApiSchema>;

/**
 * Fixer.io schemas
 * Docs: https://fixer.io/documentation
 * Example:
 * {
 *   "success": true,
 *   "base": "EUR",
 *   "date": "2018-02-22",
 *   "rates": {"USD": 1.23, ...}
 * }
 */
export const FixerSchema = z.object({
  success: z.boolean(),
  base: z.string(),
  date: z.string(), // YYYY-MM-DD
  rates: z.record(z.string(), z.number()),
});
export type FixerResponse = z.infer<typeof FixerSchema>;

/**
 * Utils
 */
export function toISODateOnly(d: Date): string {
  const nd = new Date(d);
  nd.setHours(0, 0, 0, 0);
  return nd.toISOString().slice(0, 10);
}

export function filterSymbols<T extends Record<string, number>>(
  rates: T,
  symbols?: ISO4217[],
): Record<ISO4217, number> {
  if (!symbols || symbols.length === 0) return rates as Record<ISO4217, number>;
  const set = new Set(symbols);
  const out: Record<ISO4217, number> = {};
  for (const [k, v] of Object.entries(rates)) {
    if (set.has(k)) out[k as ISO4217] = v;
  }
  return out;
}
